import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { authApi } from '@/api/auth'
import { userApi } from '@/api/user'
import type { LoginRequest, RegisterRequest, UserInfo } from '@/types/api'
import { storage } from '@/utils/storage'

export const useUserStore = defineStore('user', () => {
  const token = ref<string | null>(storage.getToken())
  const userInfo = ref<UserInfo | null>(storage.getUserInfo<UserInfo>())

  const isLoggedIn = computed(() => !!token.value && !storage.isTokenExpired())

  function setLoginState(t: string, expiresAt: number, info: UserInfo) {
    token.value = t
    userInfo.value = info
    storage.setToken(t, expiresAt)
    storage.setUserInfo(info)
  }

  function clear() {
    token.value = null
    userInfo.value = null
    storage.clearToken()
    storage.clearUserInfo()
  }

  async function register(data: RegisterRequest) {
    return authApi.register(data)
  }

  async function login(data: LoginRequest) {
    const resp = await authApi.login(data)
    setLoginState(resp.token, resp.expiresAt, resp.userInfo)
    return resp
  }

  async function logout() {
    try {
      await authApi.logout()
    } finally {
      clear()
      // 清掉本地浏览量 / 访问量去重缓存，避免换账号后被 1 小时去重影响计数
      try {
        localStorage.removeItem('forum_view_tracker_v2')
        // 兼容旧版本残留
        localStorage.removeItem('forum_view_tracker_v1')
      } catch {
        // ignore
      }
    }
  }

  /** 从后端拉取最新用户信息，刷新到 store 中。 */
  async function refreshUserInfo() {
    if (!token.value) return null
    const info = await userApi.me()
    userInfo.value = info
    storage.setUserInfo(info)
    return info
  }

  return {
    token,
    userInfo,
    isLoggedIn,
    setLoginState,
    register,
    login,
    logout,
    refreshUserInfo,
    clear,
  }
})
